import React, { useEffect, useState } from 'react';
import { Table, Button, Space, Card, Modal, Form, Input, Select, message, Typography, Tag, InputNumber, Popconfirm, Row, Col, Grid } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import client from '../api/client';
import { useAuth } from '../context/AuthContext';

const { Title } = Typography;
const { Option } = Select;

const ItemsPage = () => {
    const screens = Grid.useBreakpoint();
    const isMobile = !screens.md;
    const { user } = useAuth();
    const isAdmin = user?.role === 'ADMIN';
    const [items, setItems] = useState([]);
    const [categories, setCategories] = useState([]);
    const [units, setUnits] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form] = Form.useForm();

    const fetchData = async () => {
        setLoading(true);
        try {
            const [itemRes, categoryRes, unitRes] = await Promise.all([
                client.get('/master/items'),
                client.get('/master/categories'),
                client.get('/master/units'),
            ]);
            setItems(itemRes.data);
            setCategories(categoryRes.data);
            setUnits(unitRes.data);
        } catch (error) {
            message.error('Failed to fetch items');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const normalizeCodePrefix = (value) => {
        const cleaned = (value || '')
            .toUpperCase()
            .replace(/[^A-Z0-9]+/g, ' ')
            .trim();
        if (!cleaned) return 'ITEM';
        const parts = cleaned.split(' ').filter(Boolean);
        let prefix = parts.map((part) => part[0]).join('');
        if (prefix.length < 3) {
            const merged = cleaned.replace(/ /g, '');
            const target = Math.min(4, Math.max(3, merged.length));
            prefix = merged.slice(0, target);
        }
        if (prefix.length < 3) {
            prefix = prefix.padEnd(3, 'X');
        }
        return prefix.slice(0, 4);
    };

    const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

    const getNextItemCode = (categoryId, ignoreId) => {
        if (!categoryId) return '';
        const category = categories.find((cat) => cat.id === categoryId);
        const prefixBase = normalizeCodePrefix(category?.slug || category?.name);
        const prefix = `${prefixBase}-`;
        const pattern = new RegExp(`^${escapeRegExp(prefix)}(\\d+)$`, 'i');
        const maxNumber = items.reduce((max, item) => {
            if (item.id === ignoreId) return max;
            const match = typeof item.code === 'string' ? item.code.match(pattern) : null;
            if (!match) return max;
            const value = parseInt(match[1], 10);
            return Number.isNaN(value) ? max : Math.max(max, value);
        }, 0);
        const nextNumber = (maxNumber + 1).toString().padStart(3, '0');
        return `${prefixBase}-${nextNumber}`;
    };

    const handleCategoryChange = (categoryId) => {
        const nextCode = getNextItemCode(categoryId, editingId);
        form.setFieldsValue({ item_category_id: categoryId, code: nextCode });
    };

    const handleUnitChange = (unitId) => {
        form.setFieldsValue({ unit_id: unitId });
    };

    const handleAdd = () => {
        setEditingId(null);
        form.resetFields();
        setIsModalVisible(true);
    };

    const handleEdit = (record) => {
        setEditingId(record.id);
        form.setFieldsValue({
            ...record,
            item_category_id: record.item_category_id || record.category?.id,
            unit_id: record.unit_id || record.unit?.id,
        });
        setIsModalVisible(true);
    };

    const deactivateItem = async (record) => {
        try {
            const payload = {
                code: record.code,
                name: record.name,
                item_category_id: record.item_category_id || record.category?.id,
                unit_id: record.unit_id || record.unit?.id,
                base_price: record.base_price,
                is_active: false,
            };
            await client.put(`/master/items/${record.id}`, payload);
            message.success('Item set to inactive');
            fetchData();
        } catch (error) {
            message.error(error.response?.data?.message || 'Failed to deactivate item');
        }
    };

    const handleDelete = async (record) => {
        try {
            await client.delete(`/master/items/${record.id}`);
            message.success('Item deleted successfully');
            fetchData();
        } catch (error) {
            if (error.response?.status === 409) {
                Modal.confirm({
                    title: 'Item linked to transactions',
                    content: 'This item is linked to other records and cannot be deleted. Set it to inactive instead?',
                    okText: 'Set Inactive',
                    cancelText: 'Cancel',
                    onOk: () => deactivateItem(record),
                });
                return;
            }
            message.error(error.response?.data?.message || 'Failed to delete item');
        }
    };

    const handleCancel = () => {
        setIsModalVisible(false);
        form.resetFields();
    };

    const onFinish = async (values) => {
        try {
            if (editingId) {
                await client.put(`/master/items/${editingId}`, values);
                message.success('Item updated successfully');
            } else {
                await client.post('/master/items', values);
                message.success('Item created successfully');
            }
            setIsModalVisible(false);
            fetchData();
        } catch (error) {
            const data = error.response?.data;
            const validationErrors = data?.errors ? Object.values(data.errors).flat().join(' ') : '';
            message.error(data?.message || validationErrors || 'Operation failed');
        }
    };

    const columns = [
        { title: 'Code', dataIndex: 'code', key: 'code' },
        { title: 'Name', dataIndex: 'name', key: 'name' },
        {
            title: 'Category',
            dataIndex: 'category',
            key: 'category',
            render: (category) => category?.name || '-',
        },
        {
            title: 'Unit',
            dataIndex: 'unit',
            key: 'unit',
            render: (unit) => unit?.abbreviation || unit?.name || '-',
        },
        { title: 'Base Price', dataIndex: 'base_price', key: 'base_price' },
        {
            title: 'Status',
            dataIndex: 'is_active',
            key: 'is_active',
            render: (isActive) => (
                <Tag color={isActive ? 'success' : 'error'}>
                    {isActive ? 'Active' : 'Inactive'}
                </Tag>
            ),
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Space size="middle">
                    <Button icon={<EditOutlined />} onClick={() => handleEdit(record)} />
                    <Popconfirm
                        title="Delete Item"
                        description="Are you sure you want to delete this item?"
                        onConfirm={() => handleDelete(record)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button icon={<DeleteOutlined />} danger />
                    </Popconfirm>
                </Space>
            ),
        }
    ].filter(col => col.key !== 'actions' || isAdmin);

    const StatCard = ({ title, value, prefix, icon, color, subText }) => (
        <Card className="premium-card" bodyStyle={{ padding: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                    <Typography.Text style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', display: 'block', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{title}</Typography.Text>
                    <Title level={3} style={{ margin: 0, fontWeight: 600, letterSpacing: '-0.5px', color: 'var(--text-primary)' }}>
                        {prefix}{value}
                    </Title>
                    {subText && <Typography.Text style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4, display: 'block' }}>{subText}</Typography.Text>}
                </div>
                <div className="stat-icon" style={{ backgroundColor: `${color}15`, color: color }}>
                    {icon}
                </div>
            </div>
        </Card>
    );

    const activeItems = items.filter(i => i.is_active).length;
    const inactiveItems = (items || []).length - activeItems;

    return (
        <div style={{ padding: isMobile ? '16px' : '24px 32px' }}>
            <div style={{ marginBottom: 32, display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 12 : 0, justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'flex-end' }}>
                <div>
                    <Title level={2} style={{ margin: 0, fontWeight: 800, color: 'var(--text-primary)' }}>Items Master Data</Title>
                    <Typography.Text style={{ color: 'var(--text-secondary)', fontSize: 16 }}>Manage your product catalog, categories, and pricing.</Typography.Text>
                </div>
                {isAdmin && (
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        size="large"
                        onClick={handleAdd}
                        style={{ height: 45, borderRadius: 10, fontWeight: 600, padding: '0 24px' }}
                    >
                        Add Item
                    </Button>
                )}
            </div>

            <Row gutter={[24, 24]} style={{ marginBottom: 32 }}>
                <Col xs={24} sm={8}>
                    <StatCard
                        title="Total Items"
                        value={items.length}
                        icon={<i className="fa-solid fa-boxes-stacked"></i>}
                        color="#3b82f6"
                        subText="Total products in catalog"
                    />
                </Col>
                <Col xs={24} sm={8}>
                    <StatCard
                        title="Active Items"
                        value={activeItems}
                        icon={<i className="fa-solid fa-check-double"></i>}
                        color="#10b981"
                        subText="Items available for trade"
                    />
                </Col>
                <Col xs={24} sm={8}>
                    <StatCard
                        title="Categories"
                        value={categories.length}
                        icon={<i className="fa-solid fa-tags"></i>}
                        color="#8b5cf6"
                        subText="Product classifications"
                    />
                </Col>
            </Row>

            <Card className="premium-card" bodyStyle={{ padding: 0 }}>
                <Table
                    columns={columns}
                    dataSource={items}
                    rowKey="id"
                    loading={loading}
                    size={isMobile ? 'small' : 'middle'}
                    scroll={isMobile ? { x: 900 } : undefined}
                    pagination={{ defaultPageSize: 12, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'] }}
                />
            </Card>

            <Modal title={editingId ? 'Edit Item' : 'Add Item'} open={isModalVisible} onCancel={handleCancel} footer={null}>
                <Form form={form} layout="vertical" onFinish={onFinish}>
                    <Form.Item name="code" label="Code" rules={[{ required: true, message: 'Please enter a code' }]}>
                        <Input placeholder="Auto-generated from category" readOnly />
                    </Form.Item>
                    <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Please enter a name' }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item
                        name="item_category_id"
                        label="Category"
                        rules={[{ required: true, message: 'Please select a category' }]}
                    >
                        <Select placeholder="Select category" onChange={handleCategoryChange}>
                            {categories.map((category) => (
                                <Option key={category.id} value={category.id}>
                                    {category.name}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item name="unit_id" label="Unit" rules={[{ required: true, message: 'Please select a unit' }]}>
                        <Select placeholder="Select unit" onChange={handleUnitChange}>
                            {units.map((unit) => (
                                <Option key={unit.id} value={unit.id}>
                                    {unit.name} ({unit.abbreviation})
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="base_price"
                        label="Base Price"
                        rules={[{ required: true, message: 'Please enter a base price' }]}
                    >
                        <InputNumber min={0} style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item name="is_active" label="Status">
                        <Select>
                            <Option value={true}>Active</Option>
                            <Option value={false}>Inactive</Option>
                        </Select>
                    </Form.Item>
                    <Form.Item>
                        <Space style={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <Button onClick={handleCancel}>Cancel</Button>
                            <Button type="primary" htmlType="submit">
                                {editingId ? 'Update' : 'Create'}
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default ItemsPage;




